import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Upload, RefreshCw, FileText, Users, Receipt } from "lucide-react";
import { apiFetch, API } from "@/utils/api";

// Import de datos (roadmap 2.4) — mismo patrón de 2 pasos que el import
// bancario: plantilla descargable → preview con validación por fila (cada
// rechazo dice POR QUÉ) → confirmación → resumen final descargable.

const IMPORT_KINDS = [
  {
    kind: "clients",
    title: "Clientes",
    icon: Users,
    description:
      "Columnas: Nombre (obligatorio), NIF, Email, Teléfono, Dirección, Ciudad, " +
      "Código Postal, Notas. Los duplicados se detectan por NIF (o por nombre " +
      "si no hay NIF) y puedes elegir omitirlos o actualizar sus datos.",
    columns: ["Fila", "Nombre", "NIF", "Email"],
    cells: (d) => [d.name, d.nif, d.email],
  },
  {
    kind: "emitted",
    title: "Facturas emitidas (históricas)",
    icon: FileText,
    description:
      "Columnas: Número, Fecha, Cliente, Base, % IVA, Total (obligatorias); " +
      "NIF Cliente, Concepto, % IRPF, Estado Cobro (cobrada/pendiente) y Fecha " +
      "Vencimiento (opcionales). El total debe cuadrar con base + IVA − IRPF " +
      "(±0,01). Entran como registradas con su asiento, conservan su número " +
      "original y NO consumen tu serie de facturación. Si el cliente no existe, " +
      "se crea.",
    columns: ["Fila", "Número", "Fecha", "Cliente", "Total"],
    cells: (d) => [d.number, d.date, d.client_name, d.total],
  },
  {
    kind: "received",
    title: "Facturas recibidas (históricas)",
    icon: Receipt,
    description:
      "Columnas: Proveedor, Número, Fecha, Base, % IVA (obligatorias); NIF " +
      "Proveedor, Total y Deducible (si/no) opcionales. Si viene Total, debe " +
      "cuadrar con base + IVA (±0,01). Entran contabilizadas con su asiento; " +
      "el pago queda pendiente de conciliación bancaria, como siempre.",
    columns: ["Fila", "Proveedor", "Número", "Fecha", "Base"],
    cells: (d) => [d.supplier_name, d.number, d.date, d.base],
  },
];

function StatusBadge({ status }) {
  if (status === "ok") {
    return <Badge className="bg-emerald-100 text-emerald-700" variant="secondary">OK</Badge>;
  }
  if (status === "duplicado") {
    return <Badge className="bg-amber-100 text-amber-700" variant="secondary">Duplicado</Badge>;
  }
  return <Badge className="bg-rose-100 text-rose-700" variant="secondary">Rechazada</Badge>;
}

function ImportSection({ spec }) {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [dupAction, setDupAction] = useState("skip");
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await apiFetch(`${API}/imports/${spec.kind}/template`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `plantilla_${spec.kind}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch {
      toast.error("No se pudo descargar la plantilla");
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    setPreview(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiFetch(`${API}/imports/${spec.kind}/preview`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || "Error al procesar el archivo", { duration: 10000 });
        return;
      }
      setPreview(data);
      // Preselección: solo las filas OK. Duplicados y errores se ven con su
      // motivo; los duplicados de clientes entran si eliges "actualizar".
      const initial = {};
      data.rows.forEach((r) => { initial[r.row] = r.status === "ok"; });
      setSelected(initial);
      setDupAction("skip");
    } catch {
      toast.error("Error al subir el archivo");
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const toggleRow = (rowNum, checked) =>
    setSelected((s) => ({ ...s, [rowNum]: checked }));

  const selectableRows = (preview?.rows || []).filter((r) =>
    r.status === "ok" ||
    (r.status === "duplicado" && spec.kind === "clients" && dupAction === "update")
  );
  const selectedCount = selectableRows.filter((r) => selected[r.row]).length;

  const confirm = async () => {
    const rows = selectableRows
      .filter((r) => selected[r.row])
      .map((r) => ({ row: r.row, data: r.data }));
    if (rows.length === 0) return;
    setBusy(true);
    try {
      const res = await apiFetch(`${API}/imports/${spec.kind}/confirm`, {
        method: "POST",
        body: JSON.stringify({ rows, duplicate_action: dupAction }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || "Error al importar", { duration: 10000 });
        return;
      }
      setResult(data);
      setPreview(null);
      const parts = [`${data.imported} importadas`];
      if (data.updated > 0) parts.push(`${data.updated} actualizadas`);
      if (data.skipped?.length) parts.push(`${data.skipped.length} omitidas`);
      if (data.rejected?.length) parts.push(`${data.rejected.length} rechazadas`);
      toast.success(`${spec.title}: ${parts.join(", ")}`);
    } catch {
      toast.error("Error al importar");
    } finally {
      setBusy(false);
    }
  };

  // Resumen final descargable: filas omitidas/rechazadas con su motivo.
  const downloadResultCsv = () => {
    const lines = [["Fila", "Resultado", "Motivos"]];
    (result.skipped || []).forEach((s) =>
      lines.push([s.row, "Omitida (duplicado)", (s.motivos || []).join(" | ")]));
    (result.rejected || []).forEach((s) =>
      lines.push([s.row, "Rechazada", (s.motivos || []).join(" | ")]));
    const csv = lines
      .map((l) => l.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";"))
      .join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `resultado_import_${spec.kind}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  };

  const Icon = spec.icon;

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-6 space-y-4"
      data-testid={`import-section-${spec.kind}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Icon className="w-5 h-5 text-slate-500" /> {spec.title}
          </h3>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">{spec.description}</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={downloadTemplate}
                  data-testid={`import-template-${spec.kind}`}>
            <Download className="w-4 h-4 mr-2" /> Plantilla
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <Button size="sm" disabled={busy}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid={`import-file-${spec.kind}`}>
            {busy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Subir CSV/Excel
          </Button>
        </div>
      </div>

      {preview && (
        <div className="space-y-3 border-t pt-4">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="font-medium">{preview.summary.total} filas:</span>
            <Badge className="bg-emerald-100 text-emerald-700" variant="secondary">
              {preview.summary.ok} correctas
            </Badge>
            <Badge className="bg-amber-100 text-amber-700" variant="secondary">
              {preview.summary.duplicados} duplicados
            </Badge>
            <Badge className="bg-rose-100 text-rose-700" variant="secondary">
              {preview.summary.errores} con errores
            </Badge>
            {spec.kind === "clients" && preview.summary.duplicados > 0 && (
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-slate-500">Duplicados:</span>
                <Select value={dupAction} onValueChange={setDupAction}>
                  <SelectTrigger className="h-8 w-56" data-testid="import-dup-action">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="skip">Omitir</SelectItem>
                    <SelectItem value="update">Actualizar el cliente existente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="border rounded-lg overflow-auto max-h-[420px]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="p-2 w-10"></th>
                  {spec.columns.map((c) => (
                    <th key={c} className="p-2 text-left">{c}</th>
                  ))}
                  <th className="p-2 text-left">Estado / Motivo</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => {
                  const selectable =
                    r.status === "ok" ||
                    (r.status === "duplicado" && spec.kind === "clients" && dupAction === "update");
                  return (
                    <tr key={r.row}
                        className={`border-t ${r.status === "error" ? "bg-rose-50" : r.status === "duplicado" ? "bg-amber-50" : ""}`}>
                      <td className="p-2">
                        {selectable && (
                          <Checkbox
                            checked={!!selected[r.row]}
                            onCheckedChange={(checked) => toggleRow(r.row, checked)}
                          />
                        )}
                      </td>
                      <td className="p-2 font-mono text-slate-500">{r.row}</td>
                      {spec.cells(r.parsed || {}).map((v, i) => (
                        <td key={i} className="p-2">{v === null || v === undefined ? "—" : String(v)}</td>
                      ))}
                      <td className="p-2">
                        <StatusBadge status={r.status} />
                        {r.motivos?.length > 0 && (
                          <div className="text-xs text-slate-600 mt-1 space-y-0.5">
                            {r.motivos.map((m, i) => <div key={i}>{m}</div>)}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setPreview(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={confirm} disabled={busy || selectedCount === 0}
                    data-testid={`import-confirm-${spec.kind}`}>
              {busy ? "Importando..." : `Importar ${selectedCount} filas`}
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div className="border-t pt-4 flex flex-wrap items-center gap-3 text-sm"
             data-testid={`import-result-${spec.kind}`}>
          <span className="font-medium text-emerald-700">
            Importación completada:
          </span>
          <span>{result.imported} importadas</span>
          {result.updated > 0 && <span>· {result.updated} actualizadas</span>}
          <span>· {result.skipped?.length || 0} omitidas</span>
          <span>· {result.rejected?.length || 0} rechazadas</span>
          {((result.skipped?.length || 0) + (result.rejected?.length || 0)) > 0 && (
            <Button variant="ghost" size="sm" onClick={downloadResultCsv}>
              <Download className="w-4 h-4 mr-1" /> Descargar detalle (CSV)
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setResult(null)}>
            Cerrar
          </Button>
        </div>
      )}
    </div>
  );
}

export default function DataImport() {
  return (
    <div className="space-y-4 max-w-5xl" data-testid="data-import">
      <p className="text-sm text-slate-500">
        Migra datos desde tu herramienta anterior (Holded, Quipu, Excel...).
        Descarga la plantilla de cada bloque, rellénala (o exporta desde tu
        herramienta con esas columnas) y súbela: verás una vista previa con
        cada fila validada antes de importar nada. Máximo 2000 filas por
        archivo; las facturas históricas no pasan por tu serie de facturación
        ni por VeriFactu — son migración documental.
      </p>
      {IMPORT_KINDS.map((spec) => (
        <ImportSection key={spec.kind} spec={spec} />
      ))}
    </div>
  );
}
